/* global BigInt */
import { formatMoney, moneyFromDecimal, moneyFromMinor, normalizeMoney } from '../../../shared/money/exact-money.js';
import { formatMoneyFromMinorUnits } from '../../../shared/lib/money.js';

// DTOs keep exact account money alongside their display value. Legacy callers normalize once here.
function accountBalanceMoney(dto) {
  return Object.prototype.hasOwnProperty.call(dto, 'accountBalanceMoney')
    ? normalizeMoney(dto.accountBalanceMoney)
    : moneyFromDecimal(dto.accountBalance, dto.accountCurrency);
}

function buildAccountBalanceCoverage(dto) {
  if (String(dto?.type || '').trim().toLowerCase() !== 'withdrawal') return 'unknown';
  const balance = accountBalanceMoney(dto);
  const amount = moneyFromMinor({ amount: dto.amountMinor, currency: dto.currency });
  if (!balance || !amount || balance.asset !== amount.asset) return 'unknown';
  const scale = Math.max(balance.scale, amount.scale);
  const balanceUnits = BigInt(balance.units) * (BigInt(10) ** BigInt(scale - balance.scale));
  const amountUnits = BigInt(amount.units) * (BigInt(10) ** BigInt(scale - amount.scale));
  return balanceUnits >= amountUnits ? 'ok' : 'insufficient';
}

function formatMoneyRowValue(amountMinor, currency, locale) {
  if (amountMinor === null || amountMinor === undefined || String(amountMinor).trim() === '') {
    return '-';
  }

  return formatMoneyFromMinorUnits(amountMinor, currency, locale);
}

function formatCalculationRows(calculation, locale) {
  if (!calculation) {
    return [];
  }

  return [
    {
      label: 'Source Amount',
      labelKey: 'transactions.sourceAmount',
      value: formatMoneyRowValue(calculation.sourceAmountMinor, calculation.sourceCurrency, locale),
    },
    {
      label: 'Gross Target Amount',
      labelKey: 'transactions.grossTargetAmount',
      value: formatMoneyRowValue(calculation.grossTargetAmountMinor, calculation.targetCurrency, locale),
    },
    {
      label: 'Fee Amount',
      labelKey: 'transactions.feeAmount',
      value: formatMoneyRowValue(calculation.feeAmountMinor, calculation.targetCurrency, locale),
    },
    {
      label: 'Net Target Amount',
      labelKey: 'transactions.netTargetAmount',
      value: formatMoneyRowValue(calculation.netTargetAmountMinor, calculation.targetCurrency, locale),
    },
    {
      label: 'Base Rate',
      labelKey: 'transactions.baseRate',
      value: calculation.baseRate || '-',
    },
    {
      label: 'Fee Pips',
      labelKey: 'transactions.feePips',
      value: calculation.feePips === null || calculation.feePips === undefined ? '-' : String(calculation.feePips),
    },
    {
      label: 'Effective Rate',
      labelKey: 'transactions.effectiveRate',
      value: calculation.effectiveRate || '-',
    },
    {
      label: 'Rounding Mode',
      labelKey: 'transactions.roundingMode',
      value: calculation.roundingMode || '-',
    },
  ];
}

function resolveAccountName(account, accountNameResolver) {
  if (!account || !account.id) {
    return '-';
  }

  return accountNameResolver({
    accountType: account.type,
    tradingServerId: account.tradingServerId,
    platform: account.platform,
  }) || '-';
}

const PAYMENT_DETAIL_LABELS = {
  name: { label: 'Bank Name', labelKey: 'bankCredentials.bankNameLabel' },
  bankName: { label: 'Bank Name', labelKey: 'bankCredentials.bankNameLabel' },
  address: { label: 'Bank Address', labelKey: 'bankCredentials.addressLabel' },
  bankAddress: { label: 'Bank Address', labelKey: 'bankCredentials.addressLabel' },
  currency: { label: 'Currency', labelKey: 'bankCredentials.currencyLabel' },
  swift: { label: 'SWIFT', labelKey: 'bankCredentials.swiftLabel' },
  iban: { label: 'IBAN', labelKey: 'bankCredentials.ibanLabel' },
  accountNumber: { label: 'Account Number', labelKey: 'bankCredentials.accountNumberLabel' },
  accountName: { label: 'Account Name', labelKey: 'bankCredentials.accountNameLabel' },
  accountAddress: { label: 'Account Address', labelKey: 'bankCredentials.accountAddressLabel' },
};

const PAYMENT_DETAIL_REQUEST_HIDDEN_FIELDS = new Set(['amount', 'currency']);

function normalizePaymentDetailName(name) {
  return String(name || '').trim();
}

function buildPaymentDetailLabel(name) {
  const normalizedName = normalizePaymentDetailName(name);
  if (!normalizedName) {
    return { label: '', labelKey: '' };
  }

  const knownLabel = PAYMENT_DETAIL_LABELS[normalizedName];
  if (knownLabel) {
    return knownLabel;
  }

  const label = normalizedName
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

  return { label, labelKey: '' };
}

function formatPaymentDetails(details, source = 'request') {
  const rows = Array.isArray(details) ? details : [];
  const hiddenFields = source === 'display' ? new Set() : PAYMENT_DETAIL_REQUEST_HIDDEN_FIELDS;

  return rows
    .map((item) => {
      const name = normalizePaymentDetailName(item?.name);
      const value = String(item?.value ?? '').trim();
      const { label, labelKey } = buildPaymentDetailLabel(name);

      return {
        label,
        labelKey,
        name,
        value,
      };
    })
    .filter((item) => item.label && item.value && !hiddenFields.has(item.name))
    .map(({ label, labelKey, value }) => ({ label, labelKey, value }));
}

export function buildTransactionDetailsModel(dto, options = {}) {
  if (!dto) {
    return null;
  }

  const tradingServerNameById = options.tradingServerNameById instanceof Map
    ? options.tradingServerNameById
    : new Map();
  const platformLabelByValue = options.platformLabelByValue instanceof Map
    ? options.platformLabelByValue
    : new Map();
  const accountNameResolver = typeof options.accountNameResolver === 'function'
    ? options.accountNameResolver
    : () => '';
  const serverName = dto.tradingServerId ? tradingServerNameById.get(dto.tradingServerId) : '';
  const platformName = dto.platform ? platformLabelByValue.get(dto.platform) : '';
  const accountName = accountNameResolver(dto) || '-';
  const sourceAccountPlatform = dto.sourceAccount?.platform
    ? platformLabelByValue.get(dto.sourceAccount.platform) || dto.sourceAccount.platform
    : '-';
  const targetAccountPlatform = dto.targetAccount?.platform
    ? platformLabelByValue.get(dto.targetAccount.platform) || dto.targetAccount.platform
    : '-';

  return {
    id: dto.id,
    parentTransactionId: dto.parentTransactionId,
    childTransactionIds: dto.childTransactionIds,
    profileId: dto.profileId,
    profileName: dto.profileName,
    profileCountry: dto.profileCountry,
    profileStatus: dto.profileStatus,
    type: dto.type,
    methodId: dto.methodId,
    amountFormatted: formatMoneyFromMinorUnits(dto.amountMinor, dto.currency, options.locale),
    status: dto.status,
    reference: dto.reference,
    accountId: dto.accountId,
    accountType: dto.accountType,
    accountName,
    accountLogin: dto.accountLogin,
    accountCurrency: dto.accountCurrency,
    tradingServerId: dto.tradingServerId,
    tradingServerName: serverName || '-',
    platform: platformName || dto.platform || '-',
    accountBalance: formatMoney(accountBalanceMoney(dto), options.locale, { fallback: '-', trimTrailingZeros: true }),
    accountBalanceCoverage: buildAccountBalanceCoverage(dto),
    accountOrderId: dto.accountOrderId,
    sourceAccount: {
      ...dto.sourceAccount,
      name: resolveAccountName(dto.sourceAccount, accountNameResolver),
      platform: sourceAccountPlatform,
    },
    targetAccount: {
      ...dto.targetAccount,
      name: resolveAccountName(dto.targetAccount, accountNameResolver),
      platform: targetAccountPlatform,
    },
    sourceAccountOrderId: dto.sourceAccountOrderId,
    targetAccountOrderId: dto.targetAccountOrderId,
    ownTransferCalculation: dto.ownTransferCalculation,
    ownTransferCalculationRows: formatCalculationRows(dto.ownTransferCalculation, options.locale),
    paymentDetails: formatPaymentDetails(dto.paymentDetails, dto.paymentDetailsSource),
    paymentExecution: dto.paymentExecution,
    source: dto.source,
  };
}
